/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
//import TaskFilter from "./TaskFilter";
import CustomDataGrid from "../../components/Shared/Table/CustomDataGrid";
import { usePerformanceDisplay } from "./hooks";
import { useStoreDispatch } from "../../store/hooks";
import { setBasicDialogOpenFor, setSelectedNavListItems } from "../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { basicDialogSelector } from "../../store/helpers/helperSelectors";
import { DIALOGOPENFOR, LOCAL_STORAGE, PerformanceLteData, PerformanceNRData } from "../../globals/globals";
import { GridRowParams } from "@mui/x-data-grid";
// import { setIsEditTask, setScheduledTask } from "../../store/api/tasks/reducer";
import { ITask } from "../../store/api/tasks/types";
// import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { externalPerformanceLTEColumns, nrRegionPerformanceColumns } from "../../components/Shared/Table/Columns";
import SearchBox from "../../components/Shared/SearchBox/SearchBox";
import { useRegionDisplay } from "../Administrations/AccessControls/Regions/hooks";
import { setSelectedGeoRegion, setStartDate } from "../../store/api/admin/reducers";
import { setRegionFilterIds } from "../../store/filters/Nodes/reducer";
import CellKpiChart from "../DeviceDetail/EmbeddedCell/CellKpisChart";
import { startDateSelector } from "../../store/api/admin/selectors";
import { resetKeyword } from "../../store/search/reducer";
import { searchKeywordSelector } from "../../store/search/selector";
import ActiveFilterChips from "../../components/Shared/Filter/ActiveFilterChips";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/Shared/Icon";
import { RadixSelectControl } from "../../components/ui/radix-select";
import { FilterX, Presentation, FileDown } from "lucide-react";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "../../components/ui/tooltip";
import { exportPagePptx } from './exportPagePptx';
import { exportPagePdf } from './exportPagePdf';
import GlobalCharts from "../../components/GlobalCharts";
import { GLOBAL_LTE_CHART_GROUPS, BATCH_DATA_KEY_MAP, GLOBAL_NR_CHART_GROUPS, NR_BATCH_DATA_KEY_MAP } from "./globalChartConfigs";
import Grid from "@mui/material/Grid";
import KpiSummaryBarChart from "./KpiSummaryBarChart";
import { RadixMultiSelect } from "../../components/ui/radix-multi-select";

const ExternalPerformance: React.FC = () => {
  const dispatch = useStoreDispatch();
  const {
    // selectedRow,
    // setSelectedRow,
    // rowClicked,
    // setRowClicked,
    data,
    isLoading,
    healthFilter,
    setHealthFilter,
    // selectedDevice,
    setIsLoading,
    selectedTab,
    setSelectedTab,
    handleResetFilter
  } = usePerformanceDisplay();

  // const [selectedRowData, setSelectedRowData] = useState<ITask>();
  // const basicDialodOpenFor = useSelector(basicDialogSelector);
  const { geoRegions, loadingGeoRegions, refetchGeoRegions } = useRegionDisplay();
  const navigate = useNavigate();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  // const { deviceRowIds } = useSelector(noOfSelectedRowSelector);
  const startDate = useSelector(startDateSelector);
  const searchKeyword = useSelector(searchKeywordSelector);
  const [showChart, setShowChart] = useState(false);
  const [chartTimeRange, setChartTimeRange] = useState<string>('lastWeek');
  const [chartTruncate, setChartTruncate] = useState<string>('day');
  const [chartRegion, setChartRegion] = useState<string[]>([]);
  // Initialise to all regions when geoRegions first loads
  useEffect(() => {
    if (geoRegions?.length && chartRegion.length === 0) {
      setChartRegion(geoRegions.map((r: any) => r.name));
    }
  }, [geoRegions]);
  const [chartRefreshTrigger, setChartRefreshTrigger] = useState(0);
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  const [batchData, setBatchData] = useState<Record<string, any[]> | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [nrBatchData, setNrBatchData] = useState<Record<string, any[]> | null>(null);
  const [nrBatchLoading, setNrBatchLoading] = useState(false);
  const [pptxPageLoading, setPptxPageLoading] = useState(false);
  const [pdfPageLoading, setPdfPageLoading]   = useState(false);

  const handleExportPage = async () => {
    if (!chartRegion.length) return;
    setPptxPageLoading(true);
    try {
      await exportPagePptx({
        tab: selectedTab as 'LTE' | 'NR',
        regions: chartRegion,
        timeRange: chartTimeRange,
        truncate: chartTruncate,
      });
    } finally {
      setPptxPageLoading(false);
    }
  };

  const handleExportPagePdf = async () => {
    if (!chartRegion.length) return;
    setPdfPageLoading(true);
    try {
      await exportPagePdf({
        tab: selectedTab as 'LTE' | 'NR',
        regions: chartRegion,
        timeRange: chartTimeRange,
        truncate: chartTruncate,
      });
    } finally {
      setPdfPageLoading(false);
    }
  };

  // Batch fetch all global chart data in a single request
  useEffect(() => {
    if (!showChart || selectedTab !== 'LTE') return;

    const fetchBatchData = async () => {
      setBatchLoading(true);
      setBatchData(null);
      try {
        const params = new URLSearchParams({ truncate: chartTruncate, time_range: chartTimeRange });
        const allSelected = !chartRegion.length || (geoRegions && chartRegion.length >= geoRegions.length);
        if (!allSelected) {
          chartRegion.forEach(r => params.append('region_name', r));
        }
        const apiBaseUrl = '/v1/charts';
        const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
        const response = await fetch(
          `${apiBaseUrl}/api/lte/global-all-charts?${params}`,
          {
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setBatchData(data);
      } catch (err) {
        console.error('Batch chart fetch failed:', err);
        setBatchData(null); // fallback: GlobalCharts will fetch individually
      } finally {
        setBatchLoading(false);
      }
    };

    fetchBatchData();
  }, [showChart, selectedTab, chartTimeRange, chartTruncate, chartRefreshTrigger, chartRegion]);

  // Batch fetch all NR global chart data
  useEffect(() => {
    if (!showChart || selectedTab !== 'NR') return;
    const fetchNrBatchData = async () => {
      setNrBatchLoading(true);
      setNrBatchData(null);
      try {
        const params = new URLSearchParams({ truncate: chartTruncate, time_range: chartTimeRange });
        const allSelected = !chartRegion.length || (geoRegions && chartRegion.length >= geoRegions.length);
        if (!allSelected) {
          chartRegion.forEach(r => params.append('region_name', r));
        }
        const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
        const response = await fetch(
          `/v1/charts/api/nr/global-all-charts?${params}`,
          {
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setNrBatchData(data);
      } catch (err) {
        console.error('NR batch chart fetch failed:', err);
        setNrBatchData(null);
      } finally {
        setNrBatchLoading(false);
      }
    };
    fetchNrBatchData();
  }, [showChart, selectedTab, chartTimeRange, chartTruncate, chartRefreshTrigger, chartRegion]);

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllFilters = useCallback(() => {
    handleClearSearch();
    handleResetFilter();
  }, [handleClearSearch, handleResetFilter]);

  // const getDataFromApi: any = data || [];
  // const rowsData = selectedDevice?.type === "LTE" ? prepareCellKpiDataGrid(getDataFromApi) : prepareNrCellKpiDataGrid(getDataFromApi);

  const handleChange = (newTab: string) => {
    setSelectedTab(newTab);
  };
  // const addScheduledTaskHandler = () => {
  //   dispatch(setIsEditTask(false));
  //   dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ADDSCHEDULEDTASK));
  //   dispatch(setScheduledTask({ nextTime: dayjs().add(1, "minute").valueOf() }));
  // };

  const handleRowClicked = async (params: GridRowParams) => {
    await refetchGeoRegions();
    const filteredGeoRegion = geoRegions.find((region) => region.name === params.row.geoRegion);
    if (!filteredGeoRegion) {
      console.warn("No matching region found for:", params.row.geoRegion);
      return;
    }
    const newGeoRegion = { name: filteredGeoRegion.name, objectKey: filteredGeoRegion.objectKey };
    dispatch(setSelectedGeoRegion(newGeoRegion));
    dispatch(setRegionFilterIds([newGeoRegion.objectKey]));
    dispatch(setSelectedNavListItems(`region:${newGeoRegion.objectKey}`));
    localStorage.setItem(LOCAL_STORAGE.CURRENTREGION, JSON.stringify(newGeoRegion));
    dispatch(resetKeyword());
    navigate("/nodes/performance", { state: { rowData: params.row, newGeoRegion, selectedTab } });
  };

  // const handleCloseButtonClicked = (value: boolean) => {
  //   setRowClicked(value);
  // };
  // const DataGridTitle = () => {
  //   return <Typography variant="caption">{`Showing ${rowsData?.length || 0} result`}</Typography>
  // }

  const timeOptions = [
    { value: String(7 * 24 * 60 * 60 * 1000), label: "Last 7 days" },
    { value: String(30 * 60 * 1000), label: "Last 30 minutes" },
    { value: String(60 * 60 * 1000), label: "Last hour" },
    { value: String(24 * 60 * 60 * 1000), label: "Last 24 hours" },
  ];

  const healthFilters = [
    { value: "all", label: "ALL" },
    { value: "good", label: "Optimal" },
    { value: "bad", label: "Degraded" },
  ];

  const defaultStartDate = 24 * 60 * 60 * 1000;

  const hasActiveFilters = (
    healthFilter !== "all" ||
    (searchKeyword && searchKeyword.length > 0) ||
    startDate !== defaultStartDate
  );

  const performanceFilters = useMemo(() => [
    {
      id: "timeRange",
      label: "Time",
      value: String(startDate),
      setValue: ((_: any) => setStartDate(defaultStartDate)) as any,
      values: timeOptions.map((o) => ({ text: o.label, value: o.value })),
    },
    {
      id: "healthFilter",
      label: "Status",
      value: healthFilter,
      setValue: ((_: any) => { setHealthFilter("all"); return { type: "NOOP" }; }) as any,
      values: healthFilters.map((f) => ({ text: f.label, value: f.value })),
    },
  ], [startDate, healthFilter]);

  const handleSelect = (value: string) => {
    if (value) {
      setIsLoading(true);
      dispatch(setStartDate(Number(value)));
    }
  };

  const location = useLocation();

  useEffect(() => {
    const previousRoute = localStorage.getItem("currentRoute");
    if (previousRoute) {
      localStorage.setItem("previousRoute", previousRoute);
    }
    localStorage.setItem("currentRoute", location.pathname);
  }, [location]);

  return (
    <>
      {pptxPageLoading && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 16,
          }}
        >
          <CircularProgress size={52} thickness={4} sx={{ color: '#fff' }} />
          <span style={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
            Generating PowerPoint&hellip; please wait
          </span>
        </div>
      )}
      {basicDialogOpenFor === DIALOGOPENFOR.CELLKPILTECHART && <CellKpiChart
        kpiData={selectedTab === "LTE" ? PerformanceLteData : PerformanceNRData}
      />}
      <div
        className="flex flex-col w-full overflow-y-auto px-2 md:px-4 pt-3 pb-10 mt-3"
        style={{ color: "var(--foreground)" }}
      >
        <div
          className="bg-card text-card-foreground shadow"
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius)",
            padding: "18px",
            boxShadow: "var(--shadow-card)",
            backgroundColor: "var(--background)",
            paddingLeft: "10px",
          }}
        >
          <div className="flex flex-col">
            <div className="pl-1">
              <Tabs value={selectedTab} onValueChange={handleChange} className="w-full">
                <div className="flex items-center gap-3">
                  <TabsList className="inline-flex h-9 w-fit shrink-0 self-start items-stretch gap-0.5 rounded-full border border-border bg-muted p-0.5 text-muted-foreground">
                    <TabsTrigger
                      value="LTE"
                      className="rounded-full !bg-transparent px-4 h-full inline-flex items-center justify-center text-sm font-medium text-muted-foreground !shadow-none transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:!bg-card data-[state=active]:text-foreground data-[state=active]:!shadow-sm"
                    >
                      LTE
                    </TabsTrigger>
                    <TabsTrigger
                      value="NR"
                      className="rounded-full !bg-transparent px-4 h-full inline-flex items-center justify-center text-sm font-medium text-muted-foreground !shadow-none transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:!bg-card data-[state=active]:text-foreground data-[state=active]:!shadow-sm"
                    >
                      NR
                    </TabsTrigger>
                  </TabsList>
                  {/* Chart/Table toggle */}
                  <TooltipProvider>
                    <div className="inline-flex rounded-md border border-border overflow-hidden shrink-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setShowChart(false)}
                            aria-label="Show Table"
                            className={`h-7 w-8 inline-flex items-center justify-center transition-colors ${
                              !showChart
                                ? "bg-primary text-primary-foreground"
                                : "bg-transparent text-muted-foreground hover:bg-accent"
                            }`}
                          >
                            <Icon name="table_chart" size={16} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Show Table</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setShowChart(true)}
                            aria-label="Show Chart"
                            className={`h-7 w-8 inline-flex items-center justify-center transition-colors ${
                              showChart
                                ? "bg-primary text-primary-foreground"
                                : "bg-transparent text-muted-foreground hover:bg-accent"
                            }`}
                          >
                            <Icon name="insert_chart_outlined" size={16} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Show Chart</TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                </div>
              </Tabs>
            </div>
            <hr className="border-border" />
            <div className="pt-4 px-1 pb-10">
              {/* Filters row */}
              {!showChart && (
                <div className="flex items-center flex-wrap gap-3 mb-3">
                  <SearchBox tooltip={"Search by Region"} placeholder="Search..." />
                  <div style={{ width: "150px" }}>
                    <RadixSelectControl
                      value={String(startDate)}
                      onValueChange={handleSelect}
                      options={timeOptions}
                      placeholder="__placeholder__"
                    />
                  </div>
                  {/* Health toggle buttons */}
                  <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
                    {healthFilters.map((filter) => (
                      <button
                        key={filter.value}
                        type="button"
                        disabled={isLoading}
                        onClick={() => setHealthFilter(filter.value)}
                        className={`px-3 text-sm font-medium transition-colors ${healthFilter === filter.value
                            ? "bg-primary text-primary-foreground"
                            : "bg-transparent text-muted-foreground hover:bg-accent"
                          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                  {/* Legend button */}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-6 w-8 shrink-0"
                    onClick={() => dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.CELLKPILTECHART))}
                    aria-label="Legend"
                  >
                    <Icon name="info_outline" size={18} />
                  </Button>
                  {/* Reset filter */}
                  {hasActiveFilters && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-8 shrink-0"
                            onClick={() => { handleClearSearch(); handleResetFilter(); }}
                            aria-label="Reset All"
                          >
                            <FilterX className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Reset All</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  )}
              </div>
              )}
              {/* Results count + Active filter chips */}
              {!showChart && (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <ActiveFilterChips
                    filters={performanceFilters}
                    resultCount={data?.length || 0}
                    searchKeyword={searchKeyword}
                    onClearSearch={handleClearSearch}
                    onClearAll={handleClearAllFilters}
                    dispatch={dispatch}
                  />
                </div>
              )}
              {showChart && (selectedTab === "LTE" || selectedTab === "NR") ? (
                <div className="space-y-3">
                  {/* Chart filter bar */}
                  <div className="rounded-xl border bg-card text-card-foreground shadow">
                    <div className="px-6 py-3 flex flex-wrap items-center justify-end gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">Region</span>
                        <RadixMultiSelect
                          options={(geoRegions || []).map((r: any) => ({ value: r.name, label: r.name }))}
                          selectedValues={chartRegion}
                          onSelectionChange={setChartRegion}
                          allLabel="All Regions"
                          placeholder="All Regions"
                          className="!text-foreground !border-input !bg-background hover:!bg-accent"
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">Time Range</span>
                        <select
                          value={chartTimeRange}
                          onChange={(e) => setChartTimeRange(e.target.value)}
                          className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="lastWeek">Last 7-days</option>
                          <option value="lastMonth">Last 30-days</option>
                          <option value="lastQuarter">Prior Quarter</option>
                          <option value="lastTwoQuarters">Prior 2 Quarters</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">Interval</span>
                        <select
                          value={chartTruncate}
                          onChange={(e) => setChartTruncate(e.target.value)}
                          className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="day">Day</option>
                          <option value="week">Week</option>
                        </select>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <button
                                onClick={handleExportPage}
                                disabled={pptxPageLoading || !chartRegion.length}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-input bg-background text-foreground shadow-sm hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {pptxPageLoading
                                  ? <CircularProgress size={14} thickness={4} sx={{ color: 'inherit' }} />
                                  : <Presentation className="h-4 w-4" />}
                              </button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{pptxPageLoading ? 'Generating…' : 'Export as PowerPoint'}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <button
                                onClick={handleExportPagePdf}
                                disabled={pdfPageLoading}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-input bg-background text-foreground shadow-sm hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {pdfPageLoading
                                  ? <CircularProgress size={14} thickness={4} sx={{ color: 'inherit' }} />
                                  : <FileDown className="h-4 w-4" />}
                              </button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{pdfPageLoading ? 'Generating…' : 'Export as PDF'}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <button
                          onClick={() => setChartRefreshTrigger(t => t + 1)}
                          title="Refresh now"
                          className="h-8 w-8 flex items-center justify-center rounded-md border border-input bg-background text-foreground shadow-sm hover:bg-accent transition-colors"
                        >
                          <RefreshIcon sx={{ fontSize: 16 }} />
                        </button>
                    </div>
                  </div>
                  {selectedTab === "LTE" && (
                    <>
                      {/* KPI summary grouped by region */}
                      <KpiSummaryBarChart timeRange={chartTimeRange} regions={chartRegion} totalRegionCount={(geoRegions || []).length} refreshTrigger={chartRefreshTrigger} />
                      {/* Chart groups */}
                      {GLOBAL_LTE_CHART_GROUPS.map((group) => (
                        <div key={group.groupTitle}>
                          <h2 className="text-lg font-semibold text-foreground mb-4 mt-4 first:mt-0">
                            {group.groupTitle}
                          </h2>
                          <Grid container spacing={3}>
                            {group.configs.map((config, idx) => {
                              const key = config.csvFilename;
                              if (fullscreenChart && fullscreenChart !== key) return null;
                              const visibleConfigs = group.configs;
                              const isLastOdd = visibleConfigs.length % 2 !== 0 && idx === visibleConfigs.length - 1;
                              const mdSize = fullscreenChart === key ? 12 : isLastOdd ? 12 : 6;
                              return (
                                <Grid item xs={12} md={mdSize} key={key}>
                                  <GlobalCharts
                                    config={config}
                                    onFullscreenToggle={() => setFullscreenChart(fullscreenChart === key ? null : key)}
                                    isFullscreen={fullscreenChart === key}
                                    timeRange={chartTimeRange}
                                    truncate={chartTruncate}
                                    refreshTrigger={chartRefreshTrigger}
                                    regions={chartRegion.length && geoRegions && chartRegion.length < geoRegions.length ? chartRegion : undefined}
                                    prefetchedData={batchData ? (batchData[BATCH_DATA_KEY_MAP[key]] ?? null) : undefined}
                                    prefetchedLoading={batchLoading}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </div>
                      ))}
                    </>
                  )}
                  {selectedTab === "NR" && (
                    <>
                      {/* NR table KPI summary — one bar per KPI, aggregated (mirrors LTE chart) */}
                      <KpiSummaryBarChart variant="nr-table" timeRange={chartTimeRange} regions={chartRegion} totalRegionCount={(geoRegions || []).length} refreshTrigger={chartRefreshTrigger} />
                      {/* NR Chart groups */}
                      {GLOBAL_NR_CHART_GROUPS.map((group) => (
                        <div key={group.groupTitle}>
                          <h2 className="text-lg font-semibold text-foreground mb-4 mt-4 first:mt-0">
                            {group.groupTitle}
                          </h2>
                          <Grid container spacing={3}>
                            {group.configs.map((config, idx) => {
                              const key = config.csvFilename;
                              if (fullscreenChart && fullscreenChart !== key) return null;
                              const visibleConfigs = group.configs;
                              const isLastOdd = visibleConfigs.length % 2 !== 0 && idx === visibleConfigs.length - 1;
                              const mdSize = fullscreenChart === key ? 12 : isLastOdd ? 12 : 6;
                              return (
                                <Grid item xs={12} md={mdSize} key={key}>
                                  <GlobalCharts
                                    config={config}
                                    onFullscreenToggle={() => setFullscreenChart(fullscreenChart === key ? null : key)}
                                    isFullscreen={fullscreenChart === key}
                                    timeRange={chartTimeRange}
                                    truncate={chartTruncate}
                                    refreshTrigger={chartRefreshTrigger}
                                    regions={chartRegion.length && geoRegions && chartRegion.length < geoRegions.length ? chartRegion : undefined}
                                    prefetchedData={nrBatchData ? (nrBatchData[NR_BATCH_DATA_KEY_MAP[key]] ?? null) : undefined}
                                    prefetchedLoading={nrBatchLoading}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <CustomDataGrid
                    tableId="externalPerformance"
                    rows={isLoading ? [] : data}
                    columns={selectedTab === "LTE" ? externalPerformanceLTEColumns : nrRegionPerformanceColumns}
                    rowCount={data?.length}
                    loading={isLoading}
                    disableColumnMenu={true}
                    getRowId={(row: any) => row.geoRegion}
                    paginationMode="client"
                    sortingMode="client"
                    onRowClick={handleRowClicked}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExternalPerformance;
